<template>
  <form
    v-circular-tab
    class="repository-collaborators-form"
    @submit.prevent="onSubmit"
  >
    <div class="repository-collaborators__caption">Add collaborator</div>
    <div class="repository-collaborators-form__email">
      <UIInputText
        v-model="emailValue"
        :error="emailError"
        label="Email"
        placeholder="Email"
        data-refid="RepositoryInviteFormEmail"
        :disabled
      />
    </div>
    <div class="repository-collaborators-form__permissions">
      <UIInputSelect
        v-model="accessType"
        :error="accessTypeError"
        label="Permissions"
        data-refid="RepositoryInviteFormAccessType"
        :disabled
        :data="accessTypes"
      />
    </div>
    <CollaboratorsFormScope
      v-model="shareRootAssetIds"
      v-model:share-scope="shareScope"
      :repository-id
      :access-type="accessTypeValue"
    />
    <div class="repository-collaborators-form__buttons">
      <div class="repository-collaborators-form__buttons-description">
        <InformationCircleIcon aria-hidden="true" />
        <span>An invitation will be sent to the email address</span>
      </div>
      <UIButton
        label="Send invite"
        :disabled="isButtonDisabled"
        :loading="disabled"
        data-refid="RepositoryInviteFormSave"
      />
    </div>
  </form>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useField, useForm } from 'vee-validate'
import { orderBy } from 'lodash'

import {
  RepositoryCollaborator,
  RepositoryInvite,
  RepositoryInvitePost,
  RepositoryStatus,
} from '@types'
import { ACCESS_TYPES, ACCESS_TYPE_OWNER, ACCESS_TYPE_READONLY, NOTIFICATION_DELAY } from '@/const'

import { rules } from '@/helpers/validate'
import { handleCatchedError } from '@/helpers/common'
import { useNotifications } from '@/plugins/notification'

import { useRepositoriesStore } from '@/store/repositories/index'
import { useRepositoriesCollaboratorsStore } from '@/store/repositories/collaborators'

import { UIButton } from '@ui/buttons'
import { UIInputSelect, UIInputText } from '@ui/fields'
import CollaboratorsFormScope from './CollaboratorsFormScope.vue'
import { InformationCircleIcon } from '@heroicons/vue/24/outline'
import { ShareScope } from '../utils/enums'

type Props = {
  repositoryId: string
  list: (RepositoryInvite | RepositoryCollaborator)[]
  disabled?: boolean
}

const props = defineProps<Props>()

const { error, progress, remove, update } = useNotifications()

const repositoriesStore = useRepositoriesStore()
const repositoriesCollaboratorsStore = useRepositoriesCollaboratorsStore()

const accessTypes = orderBy(
  ACCESS_TYPES.filter((_, index) => index !== ACCESS_TYPE_OWNER - 1).map(
    (label, index) => ({
      value: `${index + 1}`,
      label,
    }),
  ),
  [item => item.value?.toLowerCase()],
)

const { meta, handleSubmit, resetForm } = useForm<RepositoryInvitePost>({
  initialValues: {
    email: '',
    access_type: 1,
    repository_id: props.repositoryId,
    share_root_asset_ids: [],
  },
  validationSchema: {
    email: rules.required,
    access_type: rules.required,
    repository_id: rules.required,
    share_root_asset_ids: true,
  },
})

const { value: emailValue, errorMessage: emailError } =
  useField<string>('email')
const { value: accessTypeValue, errorMessage: accessTypeError } =
  useField<number>('access_type')
const { value: shareRootAssetIds } = useField<string[]>('share_root_asset_ids')

const shareScope = ref<ShareScope>(ShareScope.WHOLE_REPO)

const accessType = computed({
  get() {
    return accessTypeValue.value.toString()
  },
  set(value) {
    accessTypeValue.value = +value
  },
})

const isButtonDisabled = computed(() => 
  props.disabled || 
  !meta.value.valid || 
  (shareScope.value === ShareScope.SELECTED_ACCOUNTS && !shareRootAssetIds.value.length)
)

const onSubmit = handleSubmit(async values => {
  resetForm()

  const collaborator = props.list?.find(
    item =>
      item.email === values.email && item.status !== RepositoryStatus.DECLINED,
  )
  if (collaborator) {
    await error({
      message: `User ${values.email} ${
        collaborator.status === RepositoryStatus.PENDING
          ? 'was already invited'
          : 'is already a collaborator'
      }`,
    })
    return
  }
  const nid = await progress({
    message: 'Sharing repository',
  })
  try {
    await repositoriesCollaboratorsStore.addInvite(values)
    await repositoriesStore.increaseColaboratorsCount(values.repository_id)
    await update(
      nid,
      {
        type: 'success',
        message: `Repository shared with ${values.email}`,
      },
      NOTIFICATION_DELAY,
    )
    shareRootAssetIds.value = []
    shareScope.value = ShareScope.WHOLE_REPO
  } catch (e) {
    await remove(nid)
    handleCatchedError(e as string, values)
  }
})

watch(accessTypeValue, value => {
  if (value === ACCESS_TYPE_READONLY) return
  shareRootAssetIds.value = []
})
</script>

<style>
.repository-collaborators-form {
  @apply grid grid-cols-1 sm:grid-cols-12 gap-2.5;
  @apply p-2.5;
  @apply border border-common-border rounded-md;

  &__email {
    @apply sm:col-span-9;
  }

  &__permissions {
    @apply sm:col-span-3;
  }

  &__buttons {
    @apply flex items-center justify-between;
    @apply col-span-full;
  }

  &__buttons-description {
    @apply flex items-center gap-1;
    @apply text-xs text-light-text;

    svg {
      @apply size-4;
    }
  }

  .repository-collaborators__caption {
    @apply col-span-full;
  }

  .ui-input-text-label {
    @apply text-xs font-medium;
  }
}
</style>
